import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useStoreStatus } from "../lib/storeStatus";
import Header from "../components/Header";
import { toast } from "sonner";
import { Crown, Wallet, Gift, Users, Ticket, ShieldQuestion, MessageSquare, Package as PackIcon, Star } from "lucide-react";
import { normaliseServerId } from "../lib/serverIdFormat";

const TABS = [
  ["overview","Overview"],["orders","Orders"],["rewards","Rewards"],["loyalty","Loyalty"],
  ["profiles","Game Profiles"],["referrals","Referrals"],["reviews","Reviews"],["support","Support"],
];

export default function MyVault() {
  const { user, refresh } = useAuth();
  const params = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const initial = (params.tab || search.get("tab") || "overview").toLowerCase();
  const valid = TABS.some(t => t[0] === initial) ? initial : "overview";
  const [tab, setTabState] = useState(valid);
  const setTab = (id) => { setTabState(id); nav(id === "overview" ? "/my-vault" : `/my-vault/${id}`, { replace: true }); };
  useEffect(() => {
    const next = (params.tab || search.get("tab") || "overview").toLowerCase();
    if (TABS.some(t => t[0] === next) && next !== tab) setTabState(next);
  }, [params.tab, search, tab]);

  return (
    <div>
      <Header/>
      <div className="max-w-7xl mx-auto pt-28 pb-20 px-4 sm:px-8">
        <div className="mono-caption">My Vault</div>
        <div className="flex flex-wrap items-end justify-between gap-3 mt-2 mb-2">
          <h1 className="font-serif text-3xl lg:text-4xl font-bold">Welcome, <span className="gold-text">{user?.first_name}</span></h1>
          <button
            data-testid="view-all-orders-btn"
            onClick={() => setTab("orders")}
            className="btn-outline-gold text-xs sm:text-sm inline-flex items-center gap-2"
          >
            <PackIcon className="w-4 h-4" /> View All Orders
          </button>
        </div>
        <div className="flex gap-2 mt-6 mb-8 tab-scroll no-scrollbar -mx-4 px-4">
          {TABS.map(([id, label]) => (
            <button key={id} data-testid={`tab-${id}`} onClick={()=>setTab(id)} className={`tab-btn whitespace-nowrap shrink-0 ${tab===id?"active":""}`}>{label}</button>
          ))}
        </div>

        {tab==="overview" && <Overview user={user}/>}
        {tab==="orders" && <Orders/>}
        {tab==="rewards" && <Rewards/>}
        {tab==="loyalty" && <Loyalty/>}
        {tab==="profiles" && <Profiles/>}
        {tab==="referrals" && <Referrals user={user}/>}
        {tab==="reviews" && <MyReviews/>}
        {tab==="support" && <Support/>}
      </div>
    </div>
  );
}

const Card = ({ title, children, icon }) => (<div className="vault-card p-6">{title && <div className="flex items-center gap-2 mono-caption mb-3">{icon}{title}</div>}{children}</div>);

function Overview({ user }) {
  const [loy, setLoy] = useState(null);
  useEffect(()=>{
    api.get("/my/loyalty").then(r=>setLoy(r.data));
  },[]);
  if (!user) return <div className="py-10 text-center text-[var(--av-text-dim)] text-sm">Loading your Vault…</div>;
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-6">
        <Card title="Apex Rewards" icon={<Wallet className="w-4 h-4"/>}>
          <div className="font-serif text-3xl font-bold purple-text">£{user.reward_available.toFixed(2)}</div>
          <div className="text-sm text-[var(--av-text-dim)]">Available</div>
          <div className="text-sm text-[var(--av-text-dim)] mt-2">Pending: £{user.reward_pending.toFixed(2)}</div>
        </Card>
        <Card title="Current Tier" icon={<Crown className="w-4 h-4"/>}>
          <div className="font-serif text-3xl font-bold gold-text">{user.loyalty_tier}</div>
          <div className="text-sm text-[var(--av-text-dim)]">{user.reward_rate}% reward rate</div>
          {loy?.next && (<><div className="progress-track mt-3"><div className="progress-fill" style={{width: `${loy.progress}%`}}/></div><div className="text-xs text-[var(--av-text-dim)] mt-2">Next: {loy.next.name} · {loy.progress.toFixed(0)}% complete</div></>)}
        </Card>
        <Card title="Lifetime Savings" icon={<Gift className="w-4 h-4"/>}>
          <div className="font-serif text-3xl font-bold gold-text">£{user.lifetime_savings.toFixed(2)}</div>
          <div className="text-sm text-[var(--av-text-dim)]">Value realised through the Vault</div>
        </Card>
      </div>
      {/* Rolling next-order coupon block intentionally removed —
          system retired per owner request Feb 2026. */}
    </div>
  );
}

function Orders() {
  const { salesPaused } = useStoreStatus();
  const [rows, setRows] = useState([]);
  const [resuming, setResuming] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [problemFor, setProblemFor] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const [pSubject, setPSubject] = useState("");
  const [pMessage, setPMessage] = useState("");
  const [pBusy, setPBusy] = useState(false);
  const [rvRating, setRvRating] = useState(5);
  const [rvTitle, setRvTitle] = useState("");
  const [rvBody, setRvBody] = useState("");
  const [rvBusy, setRvBusy] = useState(false);
  const load = () => api.get("/my/orders").then(r=>setRows(r.data));
  useEffect(()=>{
    load();
    // Auto-refresh order status every 15s while the page is open so the customer
    // sees WAITING FOR FULFILMENT → PROCESSING → COMPLETED transitions without reloading.
    const iv = setInterval(load, 15000);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); };
  }, []);
  const confirmRecv = async (id) => {
    try {
      await api.post(`/my/orders/${id}/confirm-delivery`, { action: "confirm" });
      toast.success("Delivery confirmed — your rewards are now available!");
      load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const submitProblem = async () => {
    if (!problemFor) return;
    if (!pSubject.trim() || !pMessage.trim()) {
      toast.error("Please add a title and describe the problem");
      return;
    }
    setPBusy(true);
    try {
      await api.post(`/my/orders/${problemFor}/confirm-delivery`, {
        action: "problem", subject: pSubject.trim(), problem_description: pMessage.trim(),
      });
      toast.success("Support ticket opened — we'll be in touch shortly.");
      setProblemFor(null); setPSubject(""); setPMessage("");
      load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed to submit"); }
    setPBusy(false);
  };
  const submitReview = async () => {
    if (!reviewFor) return;
    if (!rvTitle.trim() || !rvBody.trim()) {
      toast.error("Please add a title and your review");
      return;
    }
    setRvBusy(true);
    try {
      await api.post("/reviews", {
        order_id: reviewFor, rating: rvRating,
        title: rvTitle.trim(), body: rvBody.trim(),
      });
      toast.success("Review posted — thank you!");
      setReviewFor(null); setRvRating(5); setRvTitle(""); setRvBody("");
      load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed to submit review"); }
    setRvBusy(false);
  };
  const resume = async (id) => {
    setResuming(id);
    try {
      const { data } = await api.post(`/my/orders/${id}/resume-payment`);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reopen payment");
      setResuming(null);
      load();
    }
  };
  const doCancel = async (id) => {
    setCancelling(id);
    try {
      await api.post(`/my/orders/${id}/cancel`);
      toast.success("Order cancelled — any reserved rewards have been returned.");
      setCancelFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel order");
    }
    setCancelling(null);
  };
  const isResumable = o => ["AWAITING PAYMENT", "PAYMENT FAILED"].includes(o.status) && o.payment_status !== "paid";
  const canCancel = o => ["AWAITING PAYMENT", "PAYMENT FAILED"].includes(o.status) && o.payment_status !== "paid";
  const canRaiseIssue = o => ["FULFILLED", "AWAITING CUSTOMER CONFIRMATION", "DELIVERED"].includes(o.status);
  const canReview = o => o.status === "DELIVERED" && !o.already_reviewed;
  return (
    <>
    <Card>
      <table className="vault-table">
        <thead><tr><th>Order</th><th>Apex</th><th>Cash</th><th>Status</th><th></th></tr></thead>
        <tbody>{rows.map(o => (
          <tr key={o.id} data-testid={`order-row-${o.id}`}>
            <td className="font-mono">{o.id}</td>
            <td>{o.package_snapshot?.apex_amount?.toLocaleString()}</td>
            <td>£{o.cash_to_pay?.toFixed(2)}</td>
            <td><span className={`pill ${isResumable(o) ? "pill-purple" : "pill-gold"}`}>{o.status}</span></td>
            <td className="whitespace-nowrap">
              {isResumable(o) && (
                <button
                  data-testid={`resume-payment-${o.id}`}
                  disabled={resuming === o.id || salesPaused}
                  title={salesPaused ? "Sales are paused — resume-payment is disabled site-wide." : undefined}
                  onClick={()=>resume(o.id)}
                  className="btn-gold text-xs mr-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >{salesPaused ? "Sales Paused" : (resuming === o.id ? "Opening…" : "Resume Payment")}</button>
              )}
              {canCancel(o) && (
                <button
                  data-testid={`cancel-order-${o.id}`}
                  disabled={cancelling === o.id}
                  onClick={()=>setCancelFor(o.id)}
                  className="text-xs text-red-400 hover:underline mr-2"
                >Cancel order</button>
              )}
              {(o.status === "AWAITING CUSTOMER CONFIRMATION" || o.customer_status === "FULFILLED") && !o.customer_received_confirmed && (
                <button
                  data-testid={`confirm-recv-${o.id}`}
                  onClick={()=>confirmRecv(o.id)}
                  className="btn-outline-gold text-xs mr-2"
                >I've received the order</button>
              )}
              {o.customer_received_confirmed && (
                <span className="text-xs text-emerald-400 mr-2">✓ Received</span>
              )}
              {canReview(o) && (
                <button
                  data-testid={`review-btn-${o.id}`}
                  onClick={()=>{ setReviewFor(o.id); setRvRating(5); setRvTitle(""); setRvBody(""); }}
                  className="text-xs text-[var(--av-gold)] hover:underline mr-2"
                >Write a review</button>
              )}
              {o.already_reviewed && (
                <span className="text-xs text-[var(--av-text-dim)] mr-2">★ Reviewed</span>
              )}
              {canRaiseIssue(o) && (
                <button
                  data-testid={`problem-btn-${o.id}`}
                  onClick={()=>{ setProblemFor(o.id); setPSubject(""); setPMessage(""); }}
                  className="text-xs text-[var(--av-purple)] hover:underline"
                >I have a problem</button>
              )}
            </td>
          </tr>))}{rows.length===0 && <tr><td colSpan="5" className="text-center text-[var(--av-text-dim)] py-6">No orders yet.</td></tr>}
        </tbody>
      </table>
    </Card>
    {problemFor && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={()=>!pBusy && setProblemFor(null)}>
        <div className="vault-card p-6 max-w-md w-full" onClick={e=>e.stopPropagation()}>
          <div className="mono-caption mb-2">Report an issue</div>
          <h3 className="font-serif text-2xl gold-text mb-1">Order {problemFor}</h3>
          <p className="text-xs text-[var(--av-text-dim)] mb-4">Tell us what went wrong. Your ticket goes straight to the owner and support team.</p>
          <label className="text-xs block mb-3">Title
            <input
              data-testid="problem-subject"
              className="input-vault mt-1"
              placeholder="Short summary (e.g. Apex not received)"
              maxLength={100}
              value={pSubject}
              onChange={e=>setPSubject(e.target.value)}
            />
          </label>
          <label className="text-xs block mb-4">What happened?
            <textarea
              data-testid="problem-message"
              rows={5}
              className="input-vault mt-1"
              placeholder="Please describe the problem in detail — include timing, in-game screenshot references, and anything else we should know."
              maxLength={2000}
              value={pMessage}
              onChange={e=>setPMessage(e.target.value)}
            />
            <span className="text-[10px] text-[var(--av-text-dim)] block text-right mt-1">{pMessage.length}/2000</span>
          </label>
          <div className="flex gap-2">
            <button type="button" disabled={pBusy} onClick={()=>setProblemFor(null)} className="btn-outline-gold text-sm flex-1">Cancel</button>
            <button
              data-testid="problem-submit"
              type="button"
              disabled={pBusy || !pSubject.trim() || !pMessage.trim()}
              onClick={submitProblem}
              className="btn-gold text-sm flex-1"
            >{pBusy ? "Sending…" : "Send to Support"}</button>
          </div>
        </div>
      </div>
    )}
    {reviewFor && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={()=>!rvBusy && setReviewFor(null)}>
        <div className="vault-card p-6 max-w-md w-full" onClick={e=>e.stopPropagation()}>
          <div className="mono-caption gold-text mb-2">Write a review</div>
          <h3 className="font-serif text-2xl gold-text mb-1">Order {reviewFor}</h3>
          <p className="text-xs text-[var(--av-text-dim)] mb-4">Your review will be published as a verified purchase on the Reviews page.</p>
          <div className="mb-3">
            <div className="text-xs mb-1">Rating</div>
            <div className="flex gap-1" data-testid="review-rating">
              {[1,2,3,4,5].map(n => (
                <button
                  key={n}
                  type="button"
                  data-testid={`review-star-${n}`}
                  onClick={()=>setRvRating(n)}
                  className={`text-3xl leading-none transition ${n<=rvRating ? "text-[var(--av-gold)]" : "text-[var(--av-text-muted)]"}`}
                  aria-label={`${n} star${n>1?"s":""}`}
                >★</button>
              ))}
            </div>
          </div>
          <label className="text-xs block mb-3">Title
            <input
              data-testid="review-title"
              className="input-vault mt-1"
              placeholder="Sum up your experience"
              maxLength={100}
              value={rvTitle}
              onChange={e=>setRvTitle(e.target.value)}
            />
          </label>
          <label className="text-xs block mb-4">Your review
            <textarea
              data-testid="review-body"
              rows={5}
              className="input-vault mt-1"
              placeholder="Tell other players how the Vault worked out for you."
              maxLength={2000}
              value={rvBody}
              onChange={e=>setRvBody(e.target.value)}
            />
            <span className="text-[10px] text-[var(--av-text-dim)] block text-right mt-1">{rvBody.length}/2000</span>
          </label>
          <div className="flex gap-2">
            <button type="button" disabled={rvBusy} onClick={()=>setReviewFor(null)} className="btn-outline-gold text-sm flex-1">Cancel</button>
            <button
              data-testid="review-submit"
              type="button"
              disabled={rvBusy || !rvTitle.trim() || !rvBody.trim()}
              onClick={submitReview}
              className="btn-gold text-sm flex-1"
            >{rvBusy ? "Posting…" : "Submit Review"}</button>
          </div>
        </div>
      </div>
    )}
    {cancelFor && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={()=>cancelling==null && setCancelFor(null)}>
        <div className="vault-card p-6 max-w-md w-full" onClick={e=>e.stopPropagation()}>
          <div className="mono-caption mb-2 text-red-400">Cancel order</div>
          <h3 className="font-serif text-2xl mb-1">Order {cancelFor}</h3>
          <p className="text-sm text-[var(--av-text-dim)] mb-4">Cancelling releases any reserved Apex Rewards back to your balance and closes the Stripe checkout. This can't be undone — you'd need to place a fresh order.</p>
          <div className="flex gap-2">
            <button type="button" disabled={cancelling===cancelFor} onClick={()=>setCancelFor(null)} className="btn-outline-gold text-sm flex-1">Keep order</button>
            <button
              data-testid="cancel-confirm"
              type="button"
              disabled={cancelling===cancelFor}
              onClick={()=>doCancel(cancelFor)}
              className="text-sm flex-1 px-4 py-2 rounded-full bg-red-500/90 hover:bg-red-500 text-white font-semibold transition disabled:opacity-60"
            >{cancelling===cancelFor ? "Cancelling…" : "Yes, cancel order"}</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function Rewards() {
  const [d, setD] = useState(null);
  useEffect(()=>{ api.get("/my/rewards").then(r=>setD(r.data)); },[]);
  if (!d) return <div className="py-10 text-center text-[var(--av-text-dim)] text-sm">Loading rewards…</div>;
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card title="Available"><div className="font-serif text-4xl purple-text">£{d.available.toFixed(2)}</div></Card>
      <Card title="Pending"><div className="font-serif text-4xl gold-text">£{d.pending.toFixed(2)}</div></Card>
      <div className="lg:col-span-3"><Card title="History"><table className="vault-table"><thead><tr><th>Date</th><th>Type</th><th>Amount</th></tr></thead><tbody>{d.history.map(t=>(<tr key={t.id}><td className="font-mono text-xs">{t.at?.slice(0,16).replace("T"," ")}</td><td>{t.type}</td><td className={t.amount<0?"text-red-400":"purple-text"}>{t.amount>=0?"+":""}£{t.amount.toFixed(2)}</td></tr>))}{d.history.length===0 && <tr><td colSpan="3" className="text-center text-[var(--av-text-dim)] py-6">No reward transactions yet.</td></tr>}</tbody></table></Card></div>
    </div>
  );
}

function Loyalty() {
  const [d, setD] = useState(null);
  useEffect(()=>{ api.get("/my/loyalty").then(r=>setD(r.data)); },[]);
  if (!d) return <div className="py-10 text-center text-[var(--av-text-dim)] text-sm">Loading loyalty…</div>;
  return (
    <div className="space-y-6">
      <Card title="Progress">
        <div className="flex justify-between mb-2"><span className="font-serif text-xl gold-text">{d.current.name}</span>{d.next && <span className="text-[var(--av-text-dim)] text-sm">Next: {d.next.name}</span>}</div>
        <div className="progress-track"><div className="progress-fill" style={{width: `${d.progress}%`}}/></div>
        <div className="text-xs text-[var(--av-text-dim)] mt-2">{d.progress.toFixed(0)}% · Qualifying spend £{d.qualifying_spend.toFixed(2)}</div>
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {d.tiers.map(t => (<div key={t.id} className={`vault-card p-4 text-center ${t.name === d.current.name ? "border-[var(--av-gold)]" : ""}`}><div style={{color: t.color}} className="text-2xl">◆</div><div className="font-serif font-bold text-sm mt-1">{t.name}</div><div className="text-xs text-[var(--av-text-dim)]">£{t.min_spend.toLocaleString()}+</div><div className="mono-caption mt-2">{t.reward_rate}%</div></div>))}
      </div>
    </div>
  );
}

function Profiles() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ profile_name:"", server_id:"", governor_uid:"", in_game_name:"", is_default:false });
  const load = () => api.get("/game-profiles").then(r=>setRows(r.data));
  useEffect(()=>{ load(); }, []);
  const add = async e => { e.preventDefault(); await api.post("/game-profiles", f); setF({ profile_name:"", server_id:"", governor_uid:"", in_game_name:"", is_default:false }); load(); toast.success("Saved"); };
  const del = async id => { await api.delete(`/game-profiles/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Your Profiles">
        <div className="space-y-3">{rows.map(p=>(<div key={p.id} className="flex justify-between p-3 rounded border border-[var(--av-border)]"><div><div className="font-semibold">{p.profile_name} {p.is_default && <span className="pill pill-gold ml-1">DEFAULT</span>}</div><div className="text-xs text-[var(--av-text-dim)]">Server {p.server_id} · UID {p.governor_uid} · {p.in_game_name}</div></div><button data-testid={`del-profile-${p.id}`} onClick={()=>del(p.id)} className="text-red-400 text-xs">Delete</button></div>))}{rows.length===0 && <div className="text-[var(--av-text-dim)] text-sm">No profiles yet.</div>}</div>
      </Card>
      <Card title="Add Profile">
        <form onSubmit={add} className="space-y-2">
          <input required placeholder="Profile name" className="input-vault" value={f.profile_name} onChange={e=>setF({...f, profile_name: e.target.value})}/>
          <div>
            <input required placeholder="Server ID (e.g. E05)" className="input-vault font-mono uppercase"
              value={f.server_id}
              onChange={e=>setF({...f, server_id: e.target.value})}
              onBlur={e=>setF({...f, server_id: normaliseServerId(e.target.value)})}/>
            <p className="text-[11px] text-[var(--av-text-muted)] mt-1 leading-snug">
              Format: <span className="font-mono text-[var(--av-gold)]">E</span> + 2-digit server number. Server 5 → <span className="font-mono text-[var(--av-gold)]">E05</span>, server 10 → <span className="font-mono text-[var(--av-gold)]">E10</span>, server 175 → <span className="font-mono text-[var(--av-gold)]">E175</span>. Auto-formats when you tab away.
            </p>
          </div>
          <div>
            <input required placeholder="Governor UID (numbers only, no spaces)" className="input-vault" value={f.governor_uid} onChange={e=>setF({...f, governor_uid: e.target.value})}/>
            <p className="text-[11px] text-[var(--av-text-muted)] mt-1 leading-snug">
              Find it in-game: tap your avatar → <span className="text-[var(--av-gold)]">Profile</span> → the ID number under your name.
            </p>
          </div>
          <input required placeholder="In-Game Name" className="input-vault" value={f.in_game_name} onChange={e=>setF({...f, in_game_name: e.target.value})}/>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_default} onChange={e=>setF({...f, is_default: e.target.checked})}/> Set as default</label>
          <button data-testid="save-new-profile" className="btn-gold text-sm">Save Profile</button>
        </form>
      </Card>
    </div>
  );
}

function Referrals({ user }) {
  const [d, setD] = useState(null);
  useEffect(()=>{ api.get("/my/referrals").then(r=>setD(r.data)); },[]);
  const copy = txt => { navigator.clipboard.writeText(txt); toast.success("Copied"); };
  const link = `${window.location.origin}/auth?mode=register&ref=${user?.referral_code}`;
  if (!d) return <div className="py-10 text-center text-[var(--av-text-dim)] text-sm">Loading referrals…</div>;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Invite & Earn" icon={<Users className="w-4 h-4"/>}>
        <div className="mono-caption mb-2">Your Referral Code</div>
        <div className="flex gap-2 mb-3"><input readOnly value={d.code} className="input-vault"/><button onClick={()=>copy(d.code)} className="btn-outline-gold text-sm">Copy</button></div>
        <div className="mono-caption mb-2">Referral Link</div>
        <div className="flex gap-2"><input readOnly value={link} className="input-vault text-xs"/><button data-testid="copy-ref-link" onClick={()=>copy(link)} className="btn-outline-gold text-sm">Copy</button></div>
        <p className="text-xs text-[var(--av-text-dim)] mt-4">Your friend receives an additional 1% Apex Rewards on their first qualifying purchase. You receive 1% Apex Rewards when their qualifying order is successfully completed.</p>
      </Card>
      <Card title="Your Referrals">
        <div className="font-serif text-3xl gold-text mb-2">£{d.earned.toFixed(2)} <span className="text-sm text-[var(--av-text-dim)] font-sans">earned</span></div>
        <div className="text-sm">{d.referrals.length} successful referrals</div>
      </Card>
    </div>
  );
}

function MyReviews() {
  const [orders, setOrders] = useState([]);
  const [f, setF] = useState({ order_id:"", rating:5, title:"", body:"" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/my/orders").then(r=>setOrders(r.data.filter(o=>o.status==="DELIVERED" && !o.already_reviewed)));
  useEffect(()=>{ load(); },[]);
  const submit = async e => {
    e.preventDefault();
    if (!f.order_id) { toast.error("Pick a delivered order to review"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", f);
      toast.success("Review posted — thank you!");
      setF({ order_id:"", rating:5, title:"", body:"" });
      load();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Card title="Submit a Review">
      <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-[rgba(212,175,55,0.06)] border border-[var(--av-gold)]/30">
        <span className="text-[var(--av-gold)] text-lg leading-none mt-0.5">✓</span>
        <div className="text-xs text-[var(--av-text-dim)]">
          <span className="text-[var(--av-gold)] font-semibold">Verified purchase required.</span>{" "}
          Only customers with a delivered Arthur's Vault order can post a review — this keeps the Reviews page 100% real.
        </div>
      </div>
      {orders.length === 0 ? (
        <div className="text-sm text-[var(--av-text-dim)] py-4">
          You don't have any delivered orders left to review yet. Once your next Apex order is delivered you'll be able to submit a verified review here.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="review-form">
          <select
            required
            data-testid="review-order-select"
            className="input-vault"
            value={f.order_id}
            onChange={e=>setF({...f, order_id: e.target.value})}
          >
            <option value="">Select delivered order…</option>
            {orders.map(o=>(
              <option key={o.id} value={o.id}>
                {o.id} · {o.package_snapshot?.apex_amount?.toLocaleString()} Apex
              </option>
            ))}
          </select>
          <div>
            <div className="text-xs mb-1">Rating</div>
            <div className="flex gap-1" data-testid="review-form-rating">
              {[1,2,3,4,5].map(n => (
                <button
                  key={n}
                  type="button"
                  data-testid={`review-form-star-${n}`}
                  onClick={()=>setF({...f, rating: n})}
                  className={`text-3xl leading-none transition ${n<=f.rating ? "text-[var(--av-gold)]" : "text-[var(--av-text-muted)]"}`}
                  aria-label={`${n} star${n>1?"s":""}`}
                >★</button>
              ))}
            </div>
          </div>
          <input
            required
            data-testid="review-form-title"
            placeholder="Title"
            className="input-vault"
            value={f.title}
            onChange={e=>setF({...f, title: e.target.value})}
          />
          <textarea
            required
            data-testid="review-form-body"
            placeholder="Your review"
            className="input-vault"
            rows="4"
            value={f.body}
            onChange={e=>setF({...f, body: e.target.value})}
          />
          <button
            data-testid="submit-review"
            disabled={busy || !f.order_id || !f.title.trim() || !f.body.trim()}
            className="btn-gold w-full sm:w-auto"
          >{busy ? "Posting…" : "Submit Review"}</button>
        </form>
      )}
    </Card>
  );
}

function Support() {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [replies, setReplies] = useState({}); // { [ticketId]: draft-string }
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ category:"Other", subject:"", message:"", order_id:"" });
  const load = () => api.get("/support/tickets").then(r=>setRows(r.data));
  useEffect(()=>{ load(); },[]);
  const submit = async e => {
    e.preventDefault();
    await api.post("/support/tickets", f);
    setF({ category:"Other", subject:"", message:"", order_id:"" });
    load();
    toast.success("Ticket opened");
  };
  const sendReply = async (tid) => {
    const msg = (replies[tid] || "").trim();
    if (!msg) return;
    setBusy(true);
    try {
      await api.post(`/support/tickets/${tid}/message`, { message: msg });
      setReplies(r => ({...r, [tid]: ""}));
      await load();
      toast.success("Reply sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    setBusy(false);
  };
  const CATS = ["Order Status","Apex Not Received","Payment Problem","Incorrect Game Details","Refund","Apex Rewards","Referral","Account","Other"];
  const pillClass = (s) => {
    if (s === "OPEN") return "pill-purple";
    if (s === "AWAITING CUSTOMER") return "pill-gold";
    if (s === "RESOLVED" || s === "CLOSED") return "pill-purple opacity-70";
    return "pill-purple";
  };
  const fmt = (iso) => (iso || "").slice(0,16).replace("T"," ");
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Open a Ticket" icon={<MessageSquare className="w-4 h-4"/>}>
        <form onSubmit={submit} className="space-y-2">
          <select className="input-vault" value={f.category} onChange={e=>setF({...f, category: e.target.value})}>{CATS.map(c=><option key={c}>{c}</option>)}</select>
          <input required placeholder="Subject" className="input-vault" value={f.subject} onChange={e=>setF({...f, subject: e.target.value})}/>
          <textarea required placeholder="Message" className="input-vault" rows="4" value={f.message} onChange={e=>setF({...f, message: e.target.value})}/>
          <button data-testid="submit-ticket" className="btn-purple">Open Ticket</button>
        </form>
      </Card>
      <Card title="Your Tickets">
        {rows.map(t => {
          const isOpen = openId === t.id;
          const messages = t.messages || [];
          const hasSupportReply = messages.some(m => m.from === "support");
          return (
            <div key={t.id}
                  data-testid={`customer-ticket-${t.id}`}
                  className="border border-[var(--av-border)] rounded-lg mb-2 overflow-hidden bg-[rgba(11,12,16,0.4)]">
              <button
                onClick={() => setOpenId(isOpen ? null : t.id)}
                data-testid={`customer-ticket-toggle-${t.id}`}
                className="w-full p-3 flex items-start justify-between gap-2 hover:bg-white/5 text-left min-w-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm break-words">{t.subject}</span>
                    {hasSupportReply && (
                      <span className="pill pill-gold text-[10px]" data-testid={`customer-ticket-reply-badge-${t.id}`}>
                        Support Replied
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--av-text-dim)] flex flex-wrap gap-2 mt-1">
                    <span>{t.category}</span>
                    {t.order_id && <span className="font-mono">· {t.order_id}</span>}
                    <span>· Updated {fmt(t.updated_at)}</span>
                  </div>
                </div>
                <span className={`pill ${pillClass(t.status)} shrink-0`}>{t.status}</span>
              </button>
              {isOpen && (
                <div className="p-3 border-t border-[var(--av-border)] space-y-3">
                  <div className="max-h-72 overflow-y-auto space-y-3 pr-1"
                        data-testid={`customer-ticket-thread-${t.id}`}>
                    {messages.map((m, i) => {
                      const isSupport = m.from === "support";
                      return (
                        <div key={m.at || `msg-${i}`}
                              className={`text-xs rounded-lg p-2.5 ${isSupport
                                ? "bg-[rgba(212,175,55,0.08)] border border-[var(--av-gold)]/40"
                                : "bg-[rgba(148,0,211,0.08)] border border-[var(--av-purple)]/40"}`}>
                          <div className={`mono-caption mb-1 ${isSupport ? "gold-text" : "purple-text"}`}>
                            {isSupport ? "Arthur's Vault Support" : "You"}
                            <span className="text-[var(--av-text-muted)] font-normal ml-2">{fmt(m.at)}</span>
                          </div>
                          <div className="text-[var(--av-text)] whitespace-pre-wrap break-words">{m.message}</div>
                        </div>
                      );
                    })}
                    {messages.length === 0 && (
                      <div className="text-xs text-[var(--av-text-dim)] italic">No messages yet.</div>
                    )}
                  </div>
                  {t.status !== "CLOSED" && (
                    <div className="space-y-2">
                      <textarea
                        data-testid={`customer-ticket-reply-input-${t.id}`}
                        rows="3"
                        placeholder="Reply to support…"
                        className="input-vault text-sm"
                        value={replies[t.id] || ""}
                        onChange={e => setReplies(r => ({...r, [t.id]: e.target.value}))}
                      />
                      <button
                        data-testid={`customer-ticket-reply-send-${t.id}`}
                        onClick={() => sendReply(t.id)}
                        disabled={busy || !(replies[t.id] || "").trim()}
                        className="btn-gold text-xs disabled:opacity-40"
                      >
                        {busy ? "Sending…" : "Send Reply"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {rows.length===0 && <div className="text-[var(--av-text-dim)] text-sm">No tickets yet.</div>}
      </Card>
    </div>
  );
}
